import type { GameId, RobotLevel } from '@strategy4kids/protocol';
import { ROBOT_LEVELS } from '@strategy4kids/protocol';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useShareLinkOpen } from './hooks/useShareLinkOpen.js';
import { AppShell } from './components/AppShell.js';
import { levelLabel } from './edition/index.js';
import type { AppScreen } from './lib/query.js';
import { parseQuery, parseScreen, screenToPath } from './lib/query.js';
import { resolveRobotStyle } from './lib/robot-style.js';
import { webOnlyGame } from './lib/web-only-games.js';
import { BackgammonScreen } from './screens/BackgammonScreen.js';
import { BridgeScreen } from './screens/BridgeScreen.js';
import { ColourDashScreen } from './screens/ColourDashScreen.js';
import { CrazyEightsScreen } from './screens/CrazyEightsScreen.js';
import { MahjongSolitaireScreen } from './screens/MahjongSolitaireScreen.js';
import { GameScreen } from './screens/GameScreen.js';
import { HeartsScreen } from './screens/HeartsScreen.js';
import { GamesScreen } from './screens/GamesScreen.js';
import { Home } from './screens/Home.js';
import { Leaderboard } from './screens/Leaderboard.js';
import { LearnScreen } from './screens/LearnScreen.js';
import { LessonScreen } from './screens/LessonScreen.js';
import { Lobby } from './screens/Lobby.js';
import type { LobbyInput } from './screens/Lobby.js';
import { OnboardingWizard } from './screens/OnboardingWizard.js';
import { OnlineGameScreen } from './screens/OnlineGameScreen.js';
import { OnlineLobby } from './screens/OnlineLobby.js';
import { ParentDashboard } from './screens/ParentDashboard.js';
import { RummyScreen } from './screens/RummyScreen.js';
import { SettingsScreen } from './screens/SettingsScreen.js';
import { StoryHarness } from './screens/StoryHarness.js';
import { PirateScreen } from './screens/PirateScreen.js';
import { WhistScreen } from './screens/WhistScreen.js';
import { createAppEngine } from './services/engine.js';
import { useBackgammonStore } from './state/backgammon-store.js';
import { useBridgeStore } from './state/bridge-store.js';
import { useColourDashStore } from './state/colour-dash-store.js';
import { useCrazyEightsStore } from './state/crazy-eights-store.js';
import { useGameStore } from './state/game-store.js';
import { useHeartsStore } from './state/hearts-store.js';
import { useMahjongSolitaireStore } from './state/mahjong-solitaire-store.js';
import type { OnlineOpenInput } from './state/online-store.js';
import { useProgressStore } from './state/progress-store.js';
import { useRummyStore } from './state/rummy-store.js';
import { usePirateStore } from './state/pirate-store.js';
import { useWhistStore } from './state/whist-store.js';

/**
 * Alias, not a second literal union: `parseScreen` / `screenToPath` are typed
 * on `AppScreen`, and two hand-maintained copies of the same list is exactly
 * the drift this app can't afford — the path maps are keyed off it.
 */
type Screen = AppScreen;

export function App() {
  const query = useMemo(() => parseQuery(), []);
  const [screen, setScreenState] = useState<Screen>(() => parseScreen(window.location.pathname));
  const [lessonId, setLessonId] = useState<string | null>(null);
  // ROBOT_LEVELS is a compile-time constant in @strategy4kids/protocol (`as const` with
  // a non-empty tuple) — narrow once at module init, re-use the asserted value
  // in component bodies so we never reach for `!`.
  const firstRobotLevel: RobotLevel = (() => {
    const first = ROBOT_LEVELS[0];
    if (!first) throw new Error('@strategy4kids/protocol: ROBOT_LEVELS is empty');
    return first;
  })();
  const [level, setLevel] = useState<RobotLevel>(firstRobotLevel);
  const onboardingDone = useProgressStore((s) => s.onboardingDone);

  const [gameId, setGameId] = useState<GameId>('chess');

  /**
   * Change screen and keep the URL in step.
   *
   * `location.search` is preserved deliberately: every audit flow and E2E spec
   * drives the app with `?engine=fake` / `?onboarding=done` / `?fen=` /
   * `?moves=`, and those change runtime behaviour — dropping them mid-session
   * would silently re-arm the real Stockfish engine and the onboarding gate.
   *
   * Screens with no canonical path (an in-flight game, a selected lesson, the
   * story harness) update state and push nothing; backing out of one lands on
   * the nearest addressable ancestor via `parseScreen`'s `home` fallback.
   */
  const navigate = useCallback((next: Screen) => {
    setScreenState(next);
    // A new screen starts at the top. Without this the page keeps the scroll offset of the screen
    // it came from, so a link tapped low on Home opened Learn part-way down (and made the audit
    // goldens depend on Home's height). `instant` because a smooth scroll would still be moving
    // when a screenshot is taken.
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    const path = screenToPath(next);
    if (path && path !== window.location.pathname) {
      window.history.pushState({ screen: next }, '', `${path}${window.location.search}`);
    }
  }, []);

  // Back/forward: re-derive the screen from whatever the browser restored.
  useEffect(() => {
    const onPop = () => setScreenState(parseScreen(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const start = (selected: RobotLevel) => {
    // Backgammon branches BEFORE the engine factory: it has no MoveEngine
    // (dice flow, not FEN+search) and no GameAdapter — its store is its own.
    if (gameId === 'backgammon') {
      useBackgammonStore.getState().start(query);
      navigate('game');
      return;
    }
    // Bridge too: four seats, its own store, no MoveEngine and no GameAdapter.
    if (gameId === 'bridge') {
      useBridgeStore.getState().start(query);
      navigate('game');
      return;
    }
    // A web-only game (only registered in a web build that switched it on): its module owns its own store.
    const webOnly = webOnlyGame(gameId);
    if (webOnly) {
      webOnly.start(query);
      navigate('game');
      return;
    }
    // Hearts: single device, its own store.
    if (gameId === 'hearts') {
      useHeartsStore.getState().start(query);
      navigate('game');
      return;
    }
    // Mahjong Solitaire: one player, single device, its own store.
    if (gameId === 'mahjong-solitaire') {
      useMahjongSolitaireStore.getState().start(query);
      navigate('game');
      return;
    }
    // Rummy: single device, its own store.
    if (gameId === 'rummy') {
      useRummyStore.getState().start(query);
      navigate('game');
      return;
    }
    // Crazy Eights: single device, its own store.
    if (gameId === 'crazy-eights') {
      useCrazyEightsStore.getState().start(query);
      navigate('game');
      return;
    }
    // Colour Dash: single device, its own store.
    if (gameId === 'colour-dash') {
      useColourDashStore.getState().start(query);
      navigate('game');
      return;
    }
    // Pirate Bridge: single device, its own store.
    if (gameId === 'pirate') {
      usePirateStore.getState().start(query);
      navigate('game');
      return;
    }
    // Whist too: single device, its own store.
    if (gameId === 'whist') {
      useWhistStore.getState().start(query);
      navigate('game');
      return;
    }
    const engine = createAppEngine(gameId, selected, query);
    useGameStore.getState().startGame(gameId, engine, selected, query.fen ?? undefined, resolveRobotStyle(query));
    navigate('game');
  };

  const selectGame = (g: GameId) => setGameId(g);

  // Native DEBUG bundle only: open a chess game straight away so a shell can be checked with nobody tapping (the constant is `false`
  // in every other build and this is removed).
  useEffect(() => {
    if (typeof __NATIVE_DEBUG__ !== 'undefined' && __NATIVE_DEBUG__) start(firstRobotLevel);
    // biome-ignore lint/correctness/useExhaustiveDependencies: runs once at launch
  }, []);

  const exit = () => {
    useGameStore.getState().engine?.quit();
    useGameStore.getState().restart();
    navigate('home');
  };

  const openLesson = (id: string) => {
    setLessonId(id);
    navigate('lesson');
  };

  const [onlineInput, setOnlineInput] = useState<OnlineOpenInput | null>(null);
  const openOnline = () => navigate('online-lobby');
  const openOnlineGame = (input: OnlineOpenInput) => {
    setOnlineInput(input);
    navigate('online-game');
  };
  const exitOnline = () => navigate('online-lobby');
  const openLeaderboard = () => navigate('leaderboard');
  const openParent = () => navigate('parent');
  const openSettings = () => navigate('settings');
  const openLobby = () => navigate('lobby');
  const goHome = () => navigate('home');
  const openFromShareLink = useCallback(() => navigate('home'), [navigate]);
  useShareLinkOpen(openFromShareLink);

  // The Topbar breadcrumb surfaces the active robot level when the player is
  // mid-game, so the home chrome doesn't need its own `<h1>` anymore. Story
  // harness and onboarding keep their own minimal chrome (no Topbar).
  const levelName = useGameStore((s) => (s.level ? levelLabel(s.level) : null));

  if (query.onboarding !== 'done' && !onboardingDone && screen === 'home') {
    return (
      <OnboardingWizard
        onDone={() => useProgressStore.getState().finishOnboarding()}
        onLevel={setLevel}
      />
    );
  }

  if (screen === 'story-harness') {
    return <StoryHarness />;
  }

  const shellProps = {
    screen,
    levelName,
    onHome: goHome,
    onLearn: () => navigate('learn'),
    onOnline: openOnline,
    onLeaderboard: openLeaderboard,
    onParent: openParent,
    onSettings: openSettings,
  } as const;

  if (screen === 'games') {
    return (
      <AppShell {...shellProps}>
        <GamesScreen
          selected={gameId}
          onSelect={(g) => {
            selectGame(g);
            navigate('home');
          }}
          onExit={() => navigate('home')}
        />
      </AppShell>
    );
  }
  if (screen === 'learn') {
    return (
      <AppShell {...shellProps}>
        <LearnScreen onOpenLesson={openLesson} onExit={() => navigate('home')} />
      </AppShell>
    );
  }
  if (screen === 'lesson') {
    return lessonId ? (
      <AppShell {...shellProps}>
        <LessonScreen lessonId={lessonId} onExit={() => navigate('learn')} />
      </AppShell>
    ) : null;
  }
  if (screen === 'online-lobby') {
    return (
      <AppShell {...shellProps}>
        <OnlineLobby onPlay={openOnlineGame} onExit={() => navigate('home')} />
      </AppShell>
    );
  }
  if (screen === 'online-game') {
    return onlineInput ? (
      <AppShell {...shellProps}>
        <OnlineGameScreen input={onlineInput} onExit={exitOnline} />
      </AppShell>
    ) : null;
  }
  if (screen === 'lobby') {
    return (
      <AppShell {...shellProps}>
        <Lobby
          // TODO(pass-and-play): wire onPlay to a real 2-player GameScreen in the follow-up release.
          onPlay={() => {}}
          onExit={() => navigate('home')}
        />
      </AppShell>
    );
  }
  if (screen === 'leaderboard') {
    return (
      <AppShell {...shellProps}>
        <Leaderboard onExit={() => navigate('home')} />
      </AppShell>
    );
  }
  if (screen === 'parent') {
    return (
      <AppShell {...shellProps}>
        <ParentDashboard onExit={() => navigate('home')} />
      </AppShell>
    );
  }
  if (screen === 'settings') {
    return (
      <AppShell {...shellProps}>
        <SettingsScreen onExit={() => navigate('home')} />
      </AppShell>
    );
  }
  return (
    <AppShell {...shellProps}>
      {screen === 'home' ? (
        <Home
          gameId={gameId}
          onOpenGames={() => navigate('games')}
          onSelectGame={selectGame}
          onStart={start}
          selected={level}
          onSelectLevel={setLevel}
          onOpenLearn={() => navigate('learn')}
          onOpenOnline={openOnline}
          onOpenLobby={openLobby}
          onOpenLeaderboard={openLeaderboard}
          onOpenParent={openParent}
          onOpenSettings={openSettings}
        />
      ) : gameId === 'bridge' ? (
        <BridgeScreen
          onExit={() => {
            useBridgeStore.getState().stop();
            navigate('home');
          }}
        />
      ) : webOnlyGame(gameId) ? (
        (() => {
          const web = webOnlyGame(gameId) as NonNullable<ReturnType<typeof webOnlyGame>>;
          return (
            <web.Screen
              onExit={() => {
                web.stop();
                navigate('home');
              }}
            />
          );
        })()
      ) : gameId === 'hearts' ? (
        <HeartsScreen
          onExit={() => {
            useHeartsStore.getState().stop();
            navigate('home');
          }}
        />
      ) : gameId === 'mahjong-solitaire' ? (
        <MahjongSolitaireScreen
          onExit={() => {
            useMahjongSolitaireStore.getState().stop();
            navigate('home');
          }}
        />
      ) : gameId === 'rummy' ? (
        <RummyScreen
          onExit={() => {
            useRummyStore.getState().stop();
            navigate('home');
          }}
        />
      ) : gameId === 'crazy-eights' ? (
        <CrazyEightsScreen
          onExit={() => {
            useCrazyEightsStore.getState().stop();
            navigate('home');
          }}
        />
      ) : gameId === 'colour-dash' ? (
        <ColourDashScreen
          onExit={() => {
            useColourDashStore.getState().stop();
            navigate('home');
          }}
        />
      ) : gameId === 'pirate' ? (
        <PirateScreen
          onExit={() => {
            usePirateStore.getState().stop();
            navigate('home');
          }}
        />
      ) : gameId === 'whist' ? (
        <WhistScreen
          onExit={() => {
            useWhistStore.getState().stop();
            navigate('home');
          }}
        />
      ) : gameId === 'backgammon' ? (
        <BackgammonScreen
          onExit={() => {
            useBackgammonStore.getState().stop();
            navigate('home');
          }}
        />
      ) : (
        <GameScreen onExit={exit} />
      )}
    </AppShell>
  );
}
